import React from "react";

/** Canonical stored method value → display label. */
export const METHOD_DISPLAY_LABELS = {
  Electrofishing: "E-Fishing",
};

/**
 * Display labels.
 * - Graph Plotly / on-screen titles: W<sub>R</sub> / W<sub>S</sub>.
 * - Table PDF headers: drawn as W + smaller R/S (same look as UI).
 * - Table Excel: plain "WR" / "WS".
 * - React tables: WrLabel / WsLabel for true subscript.
 */
export const LABEL = {
  NUM: "Num.",
  /** Left axis on Number Frequency dual-Y chart (full name, not "Num."). */
  NUMBER_FREQUENCY: "Number Frequency",
  /** PSD table count column — keep ASCII-only (PDF fonts break on ≥). */
  NUM_STOCK: "Num.",
  /** Dense table header (bin summaries). */
  CUM_PCT: "Cum. %",
  /** Full wording for population graph titles / axes (client PDF feedback). */
  PCT_OF_POPULATION: "Percent of Population",
  CUM_PCT_OF_POPULATION: "Cumulative Percent of Population",
  STD_DEV: "Std Dev.",
  /** Plain-text fallback (filenames, Excel) — never use underscore W_R. */
  WR_ASCII: "WR",
  WS_ASCII: "WS",
  AVG_WR_ASCII: "Average WR",
  STD_DEV_WR_ASCII: "Std Dev. WR",
  AVERAGE_WR_ASCII: "Average WR",
  FISH_CAUGHT_PER_ANGLER_HR: "#Fish/angler*hr",
  NUM_ANGLERS: "# Anglers",
  E_FISHING: "E-Fishing",
  /** Plotly axis / HTML titles — W with uppercase subscript R/S. */
  WR_HTML: "W<sub>R</sub>",
  WS_HTML: "W<sub>S</sub>",
  AVG_WR_HTML: "Average W<sub>R</sub>",
  WR_PCT_HTML: "W<sub>R</sub> (%)",
  AVG_WR_PCT_HTML: "Average W<sub>R</sub> (%)",
  WR_AXIS_HTML: "W<sub>R</sub> (%)",
  AVG_WR_AXIS_HTML: "Average W<sub>R</sub> (%)",
  STANDARD_WR_TRACE: "Standard (W<sub>R</sub> = 100%)",
  STANDARD_WR_TRACE_PLAIN: "Standard (WR = 100%)",
  STANDARD_WS_TRACE: "Standard (W<sub>S</sub>)",
};

/**
 * Display label for sampling/capture method. Values stay "Electrofishing" in storage.
 * @param {string} method
 * @returns {string}
 */
export function displayMethodLabel(method) {
  if (method == null || method === "") return "";
  const key = String(method).trim();
  return METHOD_DISPLAY_LABELS[key] || key;
}

/**
 * Species label for dense tables only. Dropdowns/titles keep the full name.
 * @param {string|null|undefined} name
 * @param {string|null|undefined} abbreviation
 */
export function displaySpeciesTableLabel(name, abbreviation) {
  const abbr = abbreviation != null ? String(abbreviation).trim() : "";
  if (abbr) return abbr;
  const n = name != null ? String(name).trim() : "";
  if (!n) return "N/A";
  const mapped = STOCKING_SPECIES_ABBR_FALLBACK[n.toLowerCase()];
  return mapped || n;
}

/** Fallback abbreviations when fish-type Abbreviation is missing (forage/stocking). */
export const STOCKING_SPECIES_ABBR_FALLBACK = {
  "largemouth bass": "LMB",
  bluegill: "BG",
  "smallmouth bass": "SMB",
  "black crappie": "BCP",
  "white crappie": "WCP",
  "channel catfish": "CCF",
  walleye: "WAE",
  "coppernose bluegill": "CBG",
  "coppernose bluegill (alabama)": "CBG",
  "green sunfish": "GSF",
  "red ear sunfish": "RSF",
  "redear sunfish": "RSF",
  "fathead minnows": "FHM",
  "fathead minnow": "FHM",
  "golden shiners": "GSH",
  "golden shiner": "GSH",
  tilapia: "TLP",
  crawdads: "CFS",
  goldfish: "GDF",
  "rainbow trout": "RBT",
  "hybrid striped bass": "HSB",
  "threadfin shad": "TFS",
  "gizzard shad": "GSD",
};

/**
 * Prefer fish-type Abbreviation, else known name→abbr map (e.g. Fathead Minnows → FHM).
 */
export function resolveSpeciesAbbreviation(name, abbreviation) {
  const abbr = abbreviation != null ? String(abbreviation).trim() : "";
  if (abbr) return abbr;
  const n = name != null ? String(name).trim() : "";
  if (!n) return "";
  return STOCKING_SPECIES_ABBR_FALLBACK[n.toLowerCase()] || "";
}

/**
 * Full name for hover tooltip when the cell shows an abbreviation.
 * @returns {string|undefined}
 */
export function speciesTooltipTitle(name, abbreviation) {
  const abbr = abbreviation != null ? String(abbreviation).trim() : "";
  const full = name != null ? String(name).trim() : "";
  if (!abbr || !full || abbr === full) return undefined;
  return full;
}

/**
 * Dense-table species cell: abbreviation when available, with full-name hover tooltip.
 */
export function SpeciesTableLabel({
  name,
  abbreviation,
  className,
  style,
  as: Component = "span",
  ...rest
}) {
  const label = displaySpeciesTableLabel(name, abbreviation);
  const title = speciesTooltipTitle(name, abbreviation);
  return (
    <Component
      className={className}
      style={title ? { cursor: "help", ...style } : style}
      title={title}
      {...rest}
    >
      {label}
    </Component>
  );
}

/**
 * Length/weight pair for Summary by Species extremes (always 2 decimal places).
 */
export function formatSpeciesExtremeLW(length, weight) {
  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    if (!Number.isFinite(n)) return "-";
    return n.toFixed(2);
  };
  return `${fmt(length)}/${fmt(weight)}`;
}

/** React: W with subscript R (relative weight). */
export function WrLabel({ className }) {
  return (
    <span className={className}>
      W<sub>R</sub>
    </span>
  );
}

/** React: W with subscript S (standard weight). */
export function WsLabel({ className }) {
  return (
    <span className={className}>
      W<sub>S</sub>
    </span>
  );
}

const WR_WS_TEXT_TOKEN = /(WR|Wr|WS|Ws)/g;

/**
 * Replace plain WR/Wr/WS/Ws with WrLabel / WsLabel for on-screen titles.
 * @param {{ children?: React.ReactNode, text?: string, className?: string }} props
 */
export function FormatWrText({ children, text, className }) {
  const raw = text != null ? text : children;
  if (raw == null || raw === false) return null;
  if (typeof raw !== "string") return raw;
  const parts = String(raw).split(WR_WS_TEXT_TOKEN);
  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part === "WR" || part === "Wr") return <WrLabel key={`wr-${i}`} />;
        if (part === "WS" || part === "Ws") return <WsLabel key={`ws-${i}`} />;
        return part;
      })}
    </span>
  );
}

/** @deprecated alias — same as FormatWrText (also formats WS). */
export const FormatWrWsText = FormatWrText;

/** Plain / export / PDF title string for Wr. */
export function wrPlain() {
  return LABEL.WR_ASCII;
}

/** Plain / export / PDF title string for Ws. */
export function wsPlain() {
  return LABEL.WS_ASCII;
}

/**
 * Measure width (jsPDF page units) of text where WR/WS is W + smaller R/S.
 * @param {import('jspdf').jsPDF} pdf
 * @param {string} text
 */
export function pdfMeasureTextWithWr(pdf, text) {
  const baseSize = pdf.internal.getFontSize();
  const subSize = baseSize * 0.72;
  const scale = pdf.internal.scaleFactor;
  let width = 0;
  const parts = String(text ?? "").split(WR_WS_TEXT_TOKEN);
  for (const part of parts) {
    if (part === "WR" || part === "Wr") {
      width += (pdf.getStringUnitWidth("W") * baseSize) / scale;
      width += (pdf.getStringUnitWidth("R") * subSize) / scale;
    } else if (part === "WS" || part === "Ws") {
      width += (pdf.getStringUnitWidth("W") * baseSize) / scale;
      width += (pdf.getStringUnitWidth("S") * subSize) / scale;
    } else if (part) {
      width += (pdf.getStringUnitWidth(part) * baseSize) / scale;
    }
  }
  return width;
}

/**
 * Draw centered text with WR/WS as W<sub>R</sub> / W<sub>S</sub>.
 * @param {import('jspdf').jsPDF} pdf
 * @param {string} text
 * @param {number} pageWidth
 * @param {number} y
 */
export function pdfTextCenteredWithWr(pdf, text, pageWidth, y) {
  const baseSize = pdf.internal.getFontSize();
  const subSize = baseSize * 0.72;
  const scale = pdf.internal.scaleFactor;
  const totalWidth = pdfMeasureTextWithWr(pdf, text);
  let x = (pageWidth - totalWidth) / 2;
  const subY = y + baseSize * 0.22 * 0.352778;
  const parts = String(text ?? "").split(WR_WS_TEXT_TOKEN);
  for (const part of parts) {
    if (part === "WR" || part === "Wr" || part === "WS" || part === "Ws") {
      const letter = part === "WR" || part === "Wr" ? "R" : "S";
      pdf.setFontSize(baseSize);
      pdf.text("W", x, y);
      x += (pdf.getStringUnitWidth("W") * baseSize) / scale;
      pdf.setFontSize(subSize);
      pdf.text(letter, x, subY);
      x += (pdf.getStringUnitWidth(letter) * subSize) / scale;
      pdf.setFontSize(baseSize);
    } else if (part) {
      pdf.setFontSize(baseSize);
      pdf.text(part, x, y);
      x += (pdf.getStringUnitWidth(part) * baseSize) / scale;
    }
  }
}

export const pdfTextCenteredWithWrWs = pdfTextCenteredWithWr;
export const pdfMeasureTextWithWrWs = pdfMeasureTextWithWr;
